import type { CountryCode } from '@crehana/ts-types';

import getBaseUrl from '@/shared/utils/getBaseUrl';

import { countryPrefixSupported } from '../constants/countryPrefix';

export interface LanguageAlternates {
  href: string;
  hrefLang: string;
}

export type GetHrefLangData = (options: {
  currentCountryPrefix?: CountryCode;
  url: string;
}) => Array<LanguageAlternates>;

export const getHrefLangData: GetHrefLangData = ({
  currentCountryPrefix = '',
  url,
}) => {
  const data: Array<{ href: string; hrefLang: string }> = [];

  // first add the hreflang for the current country. It must be the first one
  if (currentCountryPrefix) {
    data.push({
      href: `${getBaseUrl(currentCountryPrefix)}${url}`,
      hrefLang: `${
        currentCountryPrefix === 'br' ? 'pt' : 'es'
      }-${currentCountryPrefix}`,
    });
  }

  const countries = countryPrefixSupported.filter(
    c => c !== currentCountryPrefix,
  );

  countries.forEach(country => {
    data.push({
      href: `${getBaseUrl(country as CountryCode)}${url}`,
      hrefLang: `${country === 'br' ? 'pt' : 'es'}-${country}`,
    });
  });

  data.push({
    href: `${getBaseUrl()}${url}`,
    hrefLang: 'es',
  });

  data.push({
    href: `${getBaseUrl()}${url}`,
    hrefLang: 'x-default',
  });

  return data;
};

export type GetAlternateLang = (options: {
  alternates: string[];
  url: string;
}) => Array<LanguageAlternates>;

export const getAlternateLang: GetAlternateLang = ({ alternates, url }) => {
  return (
    alternates
      // Generate the alternate languages based on the languages available for the blog post
      .map(_alternate => {
        // We need to handle this case "es" carefully because "es" should not be added as a prefix
        if (_alternate === 'es') {
          return {
            href: `${getBaseUrl()}${url}`,
            hrefLang: _alternate,
          };
        }

        return {
          href: `${getBaseUrl(_alternate as CountryCode)}${url}`,
          hrefLang: _alternate,
        };
      })
      // Add the default language
      .concat({
        href: `${getBaseUrl()}${url}`,
        hrefLang: 'x-default',
      })
  );
};
